import React, {useState, useEffect, useCallback} from 'react';
import {getConnectionStatus} from '../services/socketService';
import {Form, Input, Select, Button, Row, Col, Space, Typography, Modal, Alert, Tooltip} from 'antd';
import {useMediaQuery} from 'react-responsive';
import {motion} from 'framer-motion';
import {useSelector} from 'react-redux';
import {CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined, EnvironmentFilled, AimOutlined} from '@ant-design/icons';
import peopleService from '../services/PeopleService';
import electorKeyValidator from '../services/ElectorKeyValidator';
import IneValidator from '../services/IneValidator';
import {store} from '../store/store';
import {addPendingForm} from '../store/reducers/formReducer';
import theme from "../theme";
import zipCodeRangeService from "../services/ZipCodeRangeService";
import {useUserSections} from '../utils/userSessionUtils';
import {MapContainer, TileLayer} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const {Option} = Select;
const {Title, Text} = Typography;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const PersonRegisterForm = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalMessage, setModalMessage] = useState('');
    const [connectionStatus, setConnectionStatus] = useState(getConnectionStatus());
    const [zipCodeRange, setzipCodeRange] = useState({});
    const [areaCodes, setAreaCodes] = useState([]);
    const [mapCenter, setMapCenter] = useState([19.4326, -99.1332]); // CDMX por defecto
    const [mapKey, setMapKey] = useState(0);
    const [checkingIne, setCheckingIne] = useState(false);
    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const sections = useUserSections();
    const entityId = auth.user?.entityId || 0;
    const municipalityId = auth.user?.municipalityId || 0;
    const districtId = auth.user?.districtId || 0;
    const districtLocal = auth.user?.districtLocal || null;

    useEffect(() => {
        fetch('/areaCodes.json')
            .then(res => res.json())
            .then(data => setAreaCodes(data))
            .catch(err => console.error('Error loading area codes:', err));

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setMapCenter([position.coords.latitude, position.coords.longitude]);
                },
                () => {
                    setMapCenter([19.4326, -99.1332]); // CDMX si no da permiso
                }
            );
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setConnectionStatus(getConnectionStatus());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchSectionData = async () => {
            try {
                const filters = {
                    entityId: {value: entityId, operator: "eq"},
                    municipalityId: {value: municipalityId, operator: "eq"},
                };
                const responseZipCodeRange = await zipCodeRangeService.getAll(filters);
                if (responseZipCodeRange.data.length > 0) {
                    setzipCodeRange(responseZipCodeRange.data[0]);
                }
            } catch (error) {
                console.error("Error fetching section data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSectionData().catch(console.error);
    }, [setzipCodeRange, entityId, municipalityId]);

    const handleSubmit = useCallback(async (values) => {
        setLoading(true);
        setModalMessage('Procesando registro...');
        setModalType('loading');
        setModalVisible(true);

        values.entityId = entityId;
        values.municipalityId = municipalityId;
        values.districtId = districtId;
        values.districtLocal = districtLocal;
        values.sectionId = parseInt(values.sectionId.toString());
        values.interiorNumber = values.interiorNumber == null || values.interiorNumber.trim() === '' ? null : values.interiorNumber;
        
        const formData = {
            ...values,
            phone: values.phone.replace(/\D/g, ''),
        };

        if (getConnectionStatus()) {
            try {
                const result = await peopleService.create(formData);
                form.resetFields();
                showModal('success', `Registro exitoso: ${result.data.name} ${result.data.firstLastName}`);
            } catch (error) {
                if (error.error) {
                    // Error de WebSocket
                    const msg = typeof error.error === 'string'
                        ? error.error
                        : 'Esta persona ya está registrada en el sistema.';
                    showModal('error', msg);
                } else if (error.errorCode === "VALIDATION_ERROR" || error.errorCode === "BAD_REQUEST") {
                    const validationErrors = error.details;
                    const errorMessages = validationErrors.flatMap(errorObj => Object.values(errorObj));
                    showModal('error', (
                        <>
                            <p><b>Por favor corrige los siguientes errores:</b></p>
                            <ul style={{textAlign: "left"}}>
                                {errorMessages.map((msg, index) => (
                                    <li key={index}>{msg}</li>
                                ))}
                            </ul>
                        </>
                    ));
                } else {
                    showModal('error', typeof error.message === 'string' ? error.message : 'Error al enviar el formulario.');
                }
            }
        } else {
            form.resetFields();
            formData['user'] = auth.user.username;
            store.dispatch(addPendingForm(formData));
            showModal('offline', 'El formulario será enviado cuando haya conexión.');
        }

        setLoading(false);
    }, [auth.user.username, form, entityId, municipalityId, districtId, districtLocal]);

    const showModal = (type, message) => {
        setModalType(type);
        setModalMessage(message);
        setModalVisible(true);
    };

    const handleCheckIne = async () => {
        const electorKey = form.getFieldValue('electorKey');
        if (!electorKey || !electorKeyValidator.validate(electorKey)) return;
        setCheckingIne(true);
        try {
            const { exists, person } = await peopleService.checkElectorKey(electorKey);
            if (exists) {
                showModal('error', `La INE "${electorKey}" ya fue registrada.`);
            } else {
                showModal('info', 'INE válida. No está registrada en el sistema.');
            }
        } catch {
            showModal('error', 'Error al verificar la clave de elector.');
        }
        setCheckingIne(false);
    };

    const validateIneMatch = () => {
        const electorKey = form.getFieldValue('electorKey');
        const name = form.getFieldValue('name');
        const firstLastName = form.getFieldValue('firstLastName');
        const secondLastName = form.getFieldValue('secondLastName');
        
        if (electorKey && name && firstLastName && secondLastName) {
            const isValid = IneValidator.validateNameMatch(electorKey, name, firstLastName, secondLastName);
            if (isValid === false) {
                return Promise.reject('No coincide con la clave INE');
            }
        }
        return Promise.resolve();
    };

    const requestLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setMapCenter([position.coords.latitude, position.coords.longitude]);
                    setMapKey(prev => prev + 1);
                },
                (error) => {
                    console.error('Error obteniendo ubicación:', error);
                }
            );
        }
    };

    return (
        <>

            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "800px",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    textAlign: 'center'
                }}
            >
                <Title level={2}>Registro de convencido</Title>

                {!connectionStatus && (
                    <Alert
                        message="Modo sin conexión a internet"
                        description="Los registros se guardarán localmente y se sincronizarán automáticamente cuando vuelvas a tener conexión."
                        type="warning"
                        showIcon
                        style={{marginBottom: '16px'}}
                    />
                )}

                <motion.div
                    initial={{opacity: 0.5}}
                    animate={{opacity: 1}}
                    transition={{duration: 0.5, repeat: Infinity, repeatType: "reverse"}}
                    style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}
                >
                    {connectionStatus ? (
                        <>
                            <CheckCircleOutlined style={{color: '#52c41a', fontSize: '16px'}}/>
                            <Text strong style={{color: '#52c41a'}}>Conectado a internet</Text>
                        </>
                    ) : (
                        <>
                            <CloseCircleOutlined style={{color: '#f5222d', fontSize: '16px'}}/>
                            <Text strong style={{color: '#f5222d'}}>Sin conexión a internet</Text>
                        </>
                    )}
                </motion.div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark="optional"
                    style={{marginTop: "20px", textAlign: "left"}}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                        }
                    }}
                >
                    <Form.Item
                        label="Clave de Elector *"
                        name="electorKey"
                        rules={[
                            {
                                required: true,
                                validator: (_, value) =>
                                    electorKeyValidator.validate(value) ?
                                        Promise.resolve() :
                                        Promise.reject(new Error('Clave de elector inválida.')),
                            },
                        ]}
                    >
                        <Input placeholder="Ingrese la clave de elector" maxLength={18} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.electorKey !== cur.electorKey}>
                        {() => {
                            const electorKey = form.getFieldValue('electorKey');
                            const isValid = electorKey && electorKeyValidator.validate(electorKey);
                            return (
                                <Row justify="end" style={{marginBottom: 16}}>
                                    <Button
                                        onClick={handleCheckIne}
                                        loading={checkingIne}
                                        disabled={!isValid}
                                        style={{
                                            background: isValid ? 'linear-gradient(135deg, #9f1717, #5c0e0e)' : undefined,
                                            borderColor: isValid ? '#5c0e0e' : undefined,
                                            color: isValid ? '#fff' : undefined,
                                        }}
                                    >
                                        Validar INE
                                    </Button>
                                </Row>
                            );
                        }}
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Nombre *"
                                name="name"
                                rules={[
                                    {required: true, message: 'El nombre es obligatorio.'},
                                    {validator: validateIneMatch}
                                ]}
                                dependencies={['electorKey', 'firstLastName', 'secondLastName']}
                            >
                                <Input placeholder="Nombre" onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                                }}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Apellido Paterno *"
                                name="firstLastName"
                                rules={[
                                    {required: true, message: 'El apellido paterno es obligatorio.'},
                                    {validator: validateIneMatch}
                                ]}
                                dependencies={['electorKey', 'name', 'secondLastName']}
                            >
                                <Input placeholder="Apellido Paterno" onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                                }}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item 
                                label={
                                    <span>
                                        Apellido Materno (o XX){' '}
                                        <Tooltip title="En caso de no contar con segundo apellido debe escribir XX">
                                            <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                        </Tooltip>
                                    </span>
                                }
                                name="secondLastName"
                                rules={[
                                    {required: true, message: 'El apellido materno es obligatorio (o XX si no tiene).'},
                                    {validator: validateIneMatch}
                                ]}
                                dependencies={['electorKey', 'name', 'firstLastName']}
                            >
                                <Input placeholder="Apellido Materno" onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                                }}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Teléfono (10 dígitos con lada) *"
                        name="phone"
                        rules={[
                            {required: true, message: 'El teléfono es obligatorio.'},
                            {
                                validator: async (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const cleanedValue = value.replace(/\D/g, '');
                                    if (cleanedValue.length !== 10) {
                                        return Promise.reject(new Error('Debe contener exactamente 10 dígitos.'));
                                    }
                                    const lada3 = cleanedValue.substring(0, 3);
                                    const lada2 = cleanedValue.substring(0, 2);
                                    const validLada3 = areaCodes.some(ac => ac.areaCode.toString() === lada3);
                                    const validLada2 = ['33', '55', '56', '81'].includes(lada2) && areaCodes.some(ac => ac.areaCode.toString() === lada2);
                                    
                                    if (!validLada3 && !validLada2) {
                                        return Promise.reject(new Error(`La lada no es válida.`));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input placeholder="5551234567" maxLength={10} onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, '');
                        }}/>
                    </Form.Item>

                    <Form.Item
                        label="Calle *"
                        name="street"
                        rules={[{required: true, message: 'La calle es obligatoria.'}]}
                    >
                        <Input placeholder="Ingrese la calle" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item
                                label="Número Exterior *"
                                name="exteriorNumber"
                                rules={[{required: true, message: 'El número exterior es obligatorio.'}]}
                            >
                                <Input placeholder="Número Exterior" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item
                                label="Número Interior"
                                name="interiorNumber"
                            >
                                <Input placeholder="Número Interior (Opcional)" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={
                                    <span>
                                        Referencia{' '}
                                        <Tooltip title="Ejemplos: 'Casa blanca con portón negro', 'Frente a la tienda Oxxo', 'Entre calle 5 y 7'">
                                            <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                        </Tooltip>
                                    </span>
                                }
                                name="reference"
                                rules={[
                                    {max: 255, message: "La referencia no puede tener más de 255 caracteres"},
                                    {required: false}
                                ]}
                            >
                                <Input placeholder="Referencia" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 8}>
                            {/* TEMPORAL: Validación de rango de código postal deshabilitada temporalmente */}
                            <Form.Item
                                label="Código Postal *"
                                name="zipCode"
                                rules={[
                                    {required: true, message: 'El código postal es obligatorio.'},
                                    {
                                        pattern: /^\d{5}$/,
                                        message: 'Debe contener exactamente 5 dígitos numéricos.'
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Código Postal"
                                    maxLength={5}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/\D/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Colonia *"
                                name="neighborhood"
                                rules={[{required: true, message: 'Este campo es obligatorio.'}]}
                            >
                                <Input placeholder="Colonia" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Municipio/Delegación *"
                                name="municipalityName"
                            >
                                <Input placeholder="Municipio/Delegación" disabled={true}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Estado *"
                        name="entityName"
                    >
                        <Input placeholder="Estado" disabled={true}/>
                    </Form.Item>

                    <Form.Item
                        label="Sección *"
                        name="sectionId"
                        initialValue={sections.length === 1 ? sections[0] : null}
                        rules={[{required: true, message: 'Debe seleccionar una sección.'}]}
                    >
                        {sections.length === 1 ? (
                            // If only one section, use Input with default value
                            <Input value={`Sección ${sections[0]}`} disabled={true}/>
                        ) : (
                            // If multiple sections, use Select
                            <Select placeholder="Seleccione una sección">
                                {sections.map((sec) => (
                                    <Option key={sec} value={sec}>{`Sección ${sec}`}</Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>

                    <Form.Item
                        label="Referenciado por (Clave INE)"
                        name="referredByIneKey"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    return electorKeyValidator.validate(value) ?
                                        Promise.resolve() :
                                        Promise.reject(new Error('Clave de elector inválida.'));
                                },
                            },
                        ]}
                    >
                        <Input placeholder="Clave INE de quien lo refirió (opcional)" maxLength={18} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                    </Form.Item>

                    {connectionStatus && (
                        <Form.Item
                            label={
                                <span>
                                    Ubicación del domicilio{' '}
                                    <Tooltip title="Mueve el mapa para centrar el marcador en la ubicación exacta del domicilio del convencido">
                                        <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                    </Tooltip>
                                </span>
                            }
                            name="latLng"
                        >
                            <div style={{height: '300px', width: '100%', position: 'relative'}}>
                                <MapContainer
                                    key={mapKey}
                                    center={mapCenter}
                                    zoom={16}
                                    style={{height: '100%', width: '100%'}}
                                    whenReady={(map) => {
                                        map.target.on('moveend', () => {
                                            const newCenter = map.target.getCenter();
                                            form.setFieldsValue({latLng: `${newCenter.lat},${newCenter.lng}`});
                                        });
                                    }}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    />
                                </MapContainer>
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 1000,
                                    pointerEvents: 'none',
                                    marginTop: '-20px'
                                }}>
                                    <EnvironmentFilled style={{fontSize: '40px', color: '#ff4d4f'}} />
                                </div>
                                <Button
                                    onClick={requestLocation}
                                    style={{
                                        position: 'absolute',
                                        bottom: '20px',
                                        right: '20px',
                                        zIndex: 1000,
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                        backgroundColor: '#9f1717',
                                        borderColor: '#9f1717'
                                    }}
                                >
                                    <AimOutlined style={{fontSize: '20px', color: '#fff'}} />
                                </Button>
                            </div>
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Space style={{width: '100%', justifyContent: 'right'}}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                disabled={loading}
                                style={{
                                    backgroundColor: connectionStatus ? '#52c41a' : '#f5222d',
                                    borderColor: connectionStatus ? '#52c41a' : '#f5222d',
                                    transition: '0.3s ease-in-out',
                                }}
                            >
                                {connectionStatus ? "Enviar registro" : "Guardar registro"}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                <Modal
                    open={modalVisible}
                    footer={null}
                    closable={false}
                    centered
                    onCancel={() => setModalVisible(false)}
                >
                    {modalType === 'success' && (
                        <div style={{textAlign: 'center'}}>
                            <CheckCircleOutlined style={{color: '#52c41a', fontSize: 48}}/>
                            <Title level={3}>Registro Exitoso</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                    {modalType === 'error' && (
                        <div style={{textAlign: 'center'}}>
                            <CloseCircleOutlined style={{color: '#f5222d', fontSize: 48}}/>
                            <Title level={3}>Error</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                    {modalType === 'offline' && (
                        <div style={{textAlign: 'center'}}>
                            <ExclamationCircleOutlined style={{color: '#faad14', fontSize: 48}}/>
                            <Title level={3}>Modo Offline</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                    {modalType === 'info' && (
                        <div style={{textAlign: 'center'}}>
                            <CheckCircleOutlined style={{color: '#52c41a', fontSize: 48}}/>
                            <Title level={3}>INE Validada</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                </Modal>
            </motion.div>
        </>
    );
};

export default PersonRegisterForm;
